import type { FilterOption } from "../types";

/**
 * Overlapping option-icon stack shown in an active chip's value slot — the same
 * visual the single/multi-select chips render for their selected option icon(s)
 * (priority flame, status dot, visit-status / type icons). Each option's
 * `pill ?? icon` sits in a small elevated, hairline-outlined tile and the tiles
 * overlap (`-space-x-1.5`), so multiple selections read as a compact stack —
 * matching the pre-migration office chips (white-ish tile, not a bare icon).
 * Icons are coerced to `size-4` so the stack stays small regardless of the
 * larger size the dropdown option rows use. Options without a visual contribute
 * nothing, so icon-less fields (category, purpose, flag…) keep their text-only
 * chip.
 */
export const ValueIconStack = ({
  options,
  max = 3,
}: {
  options: FilterOption[];
  max?: number;
}) => {
  const visuals = options
    .map((option) => ({
      value: option.value,
      node: option.pill ?? option.icon,
    }))
    .filter((visual) => visual.node);

  if (visuals.length === 0) return null;

  return (
    <div className="flex items-center flex-row shrink-0 -space-x-1.5 [&_svg]:size-4">
      {visuals.slice(0, max).map((visual) => (
        <div
          key={visual.value}
          className="flex items-center justify-center shrink-0 rounded-sm bg-bg-surface-elevate-low outline outline-outline-dark-min"
        >
          {visual.node}
        </div>
      ))}
    </div>
  );
};
